import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import CrmModal from '../components/CrmModal';
import { IconPlus, IconEdit, IconTrash, IconReference } from '../components/SvgIcons';

export default function ReferenceManagement({ onReferencesUpdated }) {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState('');

  const [form, setForm] = useState({
    sourceName: '',
    description: '',
    status: 'Active',
  });

  const loadReferences = () => {
    setLoading(true);
    api.get('/api/client-module/references')
      .then((data) => {
        setReferences(data || []);
        onReferencesUpdated && onReferencesUpdated(data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadReferences, []);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.sourceName.trim()) return;

    try {
      if (editing) {
        await api.put(`/api/client-module/references/${editing.id}`, form);
        setToast('Reference source updated successfully');
      } else {
        await api.post('/api/client-module/references', form);
        setToast('Reference source added successfully');
      }
      setShowModal(false);
      setEditing(null);
      resetForm();
      loadReferences();
    } catch (err) {
      alert(err.message || 'Failed to save reference source');
    }
  };

  const resetForm = () => {
    setForm({
      sourceName: '',
      description: '',
      status: 'Active',
    });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete reference source "${name}"?`)) return;
    try {
      await api.del(`/api/client-module/references/${id}`);
      setToast('Reference source deleted successfully');
      loadReferences();
    } catch (err) {
      alert(err.message || 'Failed to delete reference source');
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      sourceName: r.sourceName || '',
      description: r.description || '',
      status: r.status || 'Active',
    });
    setShowModal(true);
  };

  return (
    <div>
      {toast && (
        <div style={{ background: 'var(--crm-emerald-light)', color: 'var(--crm-emerald)', padding: '12px 18px', borderRadius: 10, marginBottom: 18, fontWeight: 700, border: '1px solid var(--crm-border)' }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--crm-text-main)', fontWeight: 800 }}>Client Reference Sources</h2>
          <div style={{ fontSize: '0.85rem', color: 'var(--crm-text-muted)' }}>Configure referral sources used for client attribution &amp; lead tracking.</div>
        </div>

        <button
          className="cm-btn-emerald"
          onClick={() => {
            setEditing(null);
            resetForm();
            setShowModal(true);
          }}
        >
          <IconPlus size={16} /> Add Reference Source
        </button>
      </div>

      <div className="cm-table-card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--crm-text-muted)', fontWeight: 600 }}>Loading reference sources…</div>
        ) : (
          <table className="cm-table">
            <thead>
              <tr>
                <th>Reference Source</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {references.map((r) => (
                <tr key={r.id || r.sourceName}>
                  <td>
                    <div style={{ fontWeight: 800, color: 'var(--crm-text-main)' }}>{r.sourceName}</div>
                  </td>
                  <td style={{ color: 'var(--crm-text-muted)' }}>{r.description || '-'}</td>
                  <td>
                    <span className={`cm-badge ${r.status === 'Active' ? 'active-client' : 'inactive-client'}`}>{r.status}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="cm-action-icon-btn" title="Edit Source" onClick={() => startEdit(r)}>
                        <IconEdit size={16} />
                      </button>
                      <button className="cm-action-icon-btn" title="Delete Source" style={{ color: 'var(--crm-text-main)' }} onClick={() => handleDelete(r.id, r.sourceName)}>
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reusable CrmModal for Reference Source */}
      {showModal && (
        <CrmModal
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Reference Source' : 'Add Reference Source'}
          subtitle="Reference channels enable lead attribution and referral reporting"
          icon={IconReference}
          maxWidth={540}
          footer={
            <>
              <button type="button" className="cm-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="cm-btn-emerald" onClick={handleSave}>Save Reference Source</button>
            </>
          }
        >
          <form onSubmit={handleSave}>
            <div className="cm-form-section-card">
              <div className="cm-field-group">
                <label>Reference Source Name *</label>
                <input required value={form.sourceName} onChange={(e) => setForm({ ...form, sourceName: e.target.value })} placeholder="e.g. BNI, Instagram, Friend Reference" />
              </div>

              <div className="cm-field-group">
                <label>Description</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details about this referral channel..." />
              </div>

              <div className="cm-field-group">
                <label>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </CrmModal>
      )}
    </div>
  );
}
